import http, { getData } from '@/api/http';
import { encryptPassword } from '@/utils/password';
import { normalizePerms } from '@/utils/perm-map';
import type { AxiosRequestConfig } from 'axios';

// 扩展 AxiosRequestConfig 类型，添加 silent 属性
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  silent?: boolean;
}

export interface LoginInput {
  username: string;
  password: string;
}

export interface LoginUser {
  id: number;
  roleId?: number | null; // 主角色ID，后端新增
  username: string;
  roleName: string | null; // 单一角色名称，来自后端返回的 role
  employeeId: number | null; // 员工ID，后端新增
}

export interface LoginResponse {
  token: string;
  user: LoginUser;
}

// 角色详情返回结构（与路由中保持一致）
interface RoleDetail {
  id: number;
  name: string;
  description?: string;
  status?: string;
  permissions: Array<{ id?: number; name: string; description?: string }>;
}

// 假设后端登录接口为 POST /auth/login，返回 { token, user }
// 如需调整，请修改此路径与返回结构映射
export const authService = {
  async login(input: LoginInput): Promise<LoginResponse> {
    // 根据提供的文档：POST /api/login，body: { username, password }
    // 先对密码进行 md5+盐 处理
    const payload = { username: input.username, password: encryptPassword(input.password) };
    const resp = await http.post('/api/login', payload);
    const raw = getData<Record<string, unknown>>(resp) as unknown;
    if (typeof raw !== 'object' || raw === null) throw new Error('登录返回格式错误');
    const obj = raw as Record<string, unknown>;
    if (obj.success === false) {
      const msg = (obj.message as string) || '登录失败';
      throw new Error(msg);
    }
    const token = obj.token as string | undefined;
    if (!token) throw new Error('登录返回缺少 token');
    const roleId = (obj.roleId as number | undefined) ?? null;
    const roleName = (obj.role as string | undefined) ?? null;
    const username = (obj.name as string | undefined) ?? input.username; // 后端返回的 name 字段作为用户名，若缺失则使用输入的 username
    const employeeId = (obj.employeeId as number | undefined) ?? null;
    const user: LoginUser = {
      id: 0,
      roleId,
      username: username,
      roleName,
      employeeId,
    };
    return { token, user };
  },
  /**
   * 注销登录，调用后端接口 POST /api/logout。
   * 成功或失败都不抛出错误给调用方，以避免阻断本地退出流程；
   * 失败时仅在控制台记录。
   */
  async logout(): Promise<void> {
    try {
      await http.post('/api/logout');
    } catch (err) {
      // 不中断本地退出流程，但记录错误便于排查
      console.warn('Logout request failed:', err);
    }
  },
  async getRolePermissions(roleId: number | null | undefined): Promise<string[]> {
    const id = roleId ?? 0;
    const resp = await http.get(`/api/roles/${id}`, { silent: true } as ExtendedAxiosRequestConfig);
    const data = getData<RoleDetail>(resp) as RoleDetail;
    const rawPerms: string[] = Array.isArray(data.permissions)
      ? data.permissions.map((p) => p.name)
      : [];
    return normalizePerms(rawPerms);
  },
};

export type AuthService = typeof authService;

export async function changePassword(
  oldPassword: string,
  newPassword: string,
): Promise<{ success: boolean; message?: string }> {
  // 对旧密码与新密码进行加密后再提交
  const oldHashed = encryptPassword(oldPassword);
  const newHashed = encryptPassword(newPassword);
  const resp = await http.post('/api/change-password', {
    oldPassword: oldHashed,
    newPassword: newHashed,
  });
  const data = getData<{ success: boolean; message?: string }>(resp);
  return data as { success: boolean; message?: string };
}
